import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { VehicleByRegistrationNumberMinimal } from '@//app/api/protected/vehicle/lookup/registration-number/[registrationNumber]/minimal';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { httpClientGetJson } from '../httpClient';
import { VEHICLE_BY_REGISTRATION_NUMBER_MINIMAL_RATE_LIMITED_CACHE_KEY } from './useVehicleByRegistrationNumberMinimalRateLimited';
import { VEHICLE_BY_REGISTRATION_NUMBER_RATE_LIMITED_CACHE_KEY } from './useVehicleByRegistrationNumberRateLimited';

interface UseGetVehicleByRegistrationNumberMinimalRateLimitedVariables {
  registrationNumber: string;
  tryResolveFullDataSilent?: boolean;
}

const useGetVehicleByRegistrationNumberMinimalRateLimited = () => {
  const apiBaseUrl = useApiBaseUrl();
  const queryClient = useQueryClient();
  const mutation = useMutation<
    VehicleByRegistrationNumberMinimal,
    Error,
    UseGetVehicleByRegistrationNumberMinimalRateLimitedVariables
  >({
    mutationFn: async (variables: UseGetVehicleByRegistrationNumberMinimalRateLimitedVariables) => {
      const { registrationNumber } = variables;
      const url = `${apiBaseUrl}/api/vehicle/lookup/registration-number/${registrationNumber}/minimal`;
      const data = await httpClientGetJson<VehicleByRegistrationNumberMinimal>(url);

      queryClient.setQueryData(
        [VEHICLE_BY_REGISTRATION_NUMBER_MINIMAL_RATE_LIMITED_CACHE_KEY, registrationNumber],
        data,
      );

      if (variables.tryResolveFullDataSilent) {
        try {
          const fullData = await httpClientGetJson(
            `${apiBaseUrl}/api/vehicle/lookup/registration-number/${registrationNumber}`,
          );
          queryClient.setQueryData(
            [
              VEHICLE_BY_REGISTRATION_NUMBER_RATE_LIMITED_CACHE_KEY,
              registrationNumber.toLowerCase(),
            ],
            fullData,
          );
        } catch (_) {
          // Ignore errors when trying to resolve full data silently
        }
      }

      return data;
    },
  });

  return mutation;
};

export default useGetVehicleByRegistrationNumberMinimalRateLimited;
